import type {
  ContactServiceInfo,
  SellerInfo,
  SellerLoginParams,
  SellerLoginResponse,
  SellerSendSmsParams,
  SellerWechatMPLoginQRCodeResponse,
  SellerWechatMPLoginStatusParams,
  SellerWechatMPLoginStatusResponse,
  SellerUpdateProfileReq,
  SendSmsResponse,
  SubmitCooperationIntentReq,
  SubmitCooperationIntentResp,
} from "@/types/seller";
import { request } from "@/utils/fetch";

// ============================================
// 卖家端 API 接口
// ============================================

/**
 * 发送短信验证码
 * @param phone 手机号
 */
export const sendSmsCode = (phone: string) => {
  const data: SellerSendSmsParams = {
    phone,
    type: 1, // 2-注册（卖家登录注册合一）
    scene: 2, // 2-卖家
  };
  return request<SendSmsResponse>({
    url: "/api/v1/supplier/sms/send",
    method: "POST",
    data,
  });
};

/**
 * 卖家登录/注册
 * @param params 登录参数
 */
export const sellerLogin = (params: SellerLoginParams) => {
  return request<SellerLoginResponse>({
    url: "/api/v1/supplier/seller/login",
    method: "POST",
    data: params,
  });
};

/**
 * 创建卖家服务号扫码登录二维码
 */
export const getSellerWechatMPLoginUrl = () => {
  return request<SellerWechatMPLoginQRCodeResponse>({
    url: "/api/v1/supplier/seller/login/url",
    method: "POST",
  });
};

/**
 * 查询卖家服务号扫码登录状态
 * @param params 状态查询参数
 */
export const getSellerWechatMPLoginStatus = (
  params: SellerWechatMPLoginStatusParams,
) => {
  return request<SellerWechatMPLoginStatusResponse>({
    url: "/api/v1/supplier/seller/login/status",
    method: "POST",
    data: params,
    silent: true,
  });
};

/**
 * 获取卖家信息
 */
export const getSellerInfo = () => {
  return request<SellerInfo>({
    url: "/api/v1/supplier/seller/info",
    method: "GET",
  });
};

/**
 * 卖家登出
 */
export const sellerLogout = () => {
  return request({
    url: "/api/v1/supplier/seller/logout",
    method: "POST",
  });
};

// ============================================
// 合作意向与联系客服 API 接口
// ============================================

/**
 * 获取联系客服信息
 * 返回客服电话和联系客服二维码图片URL，无需鉴权
 */
export const getContactService = () => {
  return request<ContactServiceInfo>({
    url: "/api/v1/supplier/contact-service",
    method: "GET",
  });
};

/**
 * 提交合作意向
 * 登录状态下可选携带 Authorization 头以记录 seller_id
 * @param params 合作意向参数
 */
export const submitCooperationIntent = (params: SubmitCooperationIntentReq) => {
  return request<SubmitCooperationIntentResp>({
    url: "/api/v1/supplier/cooperation-intent/submit",
    method: "POST",
    data: params,
  });
};

/**
 * 更新卖家个人信息（昵称、头像）
 * 昵称会进行敏感词审核，头像需通过通用上传接口上传
 * @param params 更新参数，至少传一个字段
 */
export const updateSellerProfile = (params: SellerUpdateProfileReq) => {
  return request({
    url: "/api/v1/supplier/seller/profile/update",
    method: "POST",
    data: params,
  });
};

// ============================================
// 地区相关 API 接口
// ============================================

/** 地区节点 */
export interface RegionNode {
  code: string;
  name: string;
  parent_code?: string;
}

/** 按父级查询地区响应 */
export interface RegionByParentResponse {
  regions: RegionNode[];
}

/** 级联选择器选项格式 */
export interface CascaderOption {
  value: string;
  label: string;
  children?: CascaderOption[];
  leaf?: boolean;
  /** 地区层级：省 0、市 1、区县 2。 */
  level?: number;
}

/**
 * 按父级代码获取直接子地区；不传 parent_code 获取省级地区。
 */
export const getRegions = (parentCode = "") => {
  return request<RegionByParentResponse>({
    url: "/api/v1/supplier/regions",
    method: "GET",
    params: parentCode ? { parent_code: parentCode } : undefined,
  });
};
